import { useCallback, useEffect, useRef, useState } from 'react'
import * as Sentry from '@sentry/react'
import { track } from '../utils/analytics.js'
import { generateSecretKey, getPublicKey, finalizeEvent } from 'nostr-tools/pure'
import * as nip19 from 'nostr-tools/nip19'
import * as nip49 from 'nostr-tools/nip49'
import {
  startGoogleOAuth,
  parseOAuthCallbackHash,
  resolveNostrIdentity,
  readLinkedDriveFile,
  writeLinkedDriveFile,
  readPrefsFile,
  persistDriveToken,
  clearDriveToken,
} from '../utils/googleAuth.js'
import { saveJurisdiction } from '../utils/jurisdiction.js'

// localStorage keys
const LS_PUBKEY = 'verdant_pubkey'
const LS_LOGIN_TYPE = 'verdant_login_type'   // 'nip07' | 'local' | 'local-encrypted' | 'google' | 'ephemeral'
const LS_NSEC = 'verdant_nsec_hex'           // plaintext hex secret ('local' and 'ephemeral')
const LS_NCRYPTSEC = 'verdant_ncryptsec'     // NIP-49 encoded blob ('local-encrypted')

// sessionStorage — decrypted secret key, per-tab, cleared on tab close.
// Used to keep the user unlocked across page refresh without writing the
// decrypted material to disk (would defeat the at-rest encryption).
const SS_DECRYPTED_KEY = 'verdant_decrypted_key'

// UX29 Phase 2c: in-flight marker set when the user starts linking an
// existing nsec account to Google. The OAuth callback useEffect reads
// it and branches into the linking flow instead of a sign-in. 10-minute
// TTL so a stale marker (user bailed mid-flow, came back later) doesn't
// hijack a legitimate fresh sign-in.
const SS_LINK_FLAG = 'verdant_google_linking'
const LINK_FLAG_TTL_MS = 10 * 60 * 1000

// Three-mode auth: NIP-07 extension, plaintext local key (Damus/Primal-style),
// or passphrase-encrypted local key (NIP-49). The secret never leaves the
// device. For encrypted accounts, the decrypted key lives only in memory
// for the session — the user re-enters their passphrase next time.
export function useNostrLogin() {
  const initial = loadInitial()
  const [pubkey, setPubkey] = useState(initial.pubkey)
  const [loginType, setLoginType] = useState(initial.type)
  const [nostrAvailable, setNostrAvailable] = useState(false)
  const [error, setError] = useState(null)

  // The decrypted secret key, held in memory only.
  const localKeyRef = useRef(null)

  // True when the on-disk record says we have an account but no
  // in-memory secret yet — happens when the user had an encrypted key
  // and hasn't entered the passphrase this session, OR when the user
  // signed in with Google in a prior tab and the sessionStorage key
  // cleared on tab close.
  const [locked, setLocked] = useState(
    initial.type === 'local-encrypted' || initial.type === 'google'
  )
  // True only while the mount-time sessionStorage restore is in flight
  // for a local-encrypted or Google account. Callers (Home.jsx) gate
  // the auto-open of the unlock modal on !restoring so the modal
  // doesn't flash open on a refresh that's about to succeed silently.
  const [restoring, setRestoring] = useState(
    initial.type === 'local-encrypted' || initial.type === 'google'
  )

  // Detect window.nostr (extensions inject after page load).
  useEffect(() => {
    const check = () =>
      setNostrAvailable(typeof window !== 'undefined' && !!window.nostr)
    check()
    const t = setInterval(check, 1000)
    return () => clearInterval(t)
  }, [])

  // For plaintext-stored accounts (local + ephemeral), hydrate the
  // in-memory key on mount.
  useEffect(() => {
    const isPlaintext = loginType === 'local' || loginType === 'ephemeral'
    if (!isPlaintext || localKeyRef.current) return
    try {
      const hex = localStorage.getItem(LS_NSEC)
      if (!hex) {
        // Inconsistent state — clear and force re-login.
        clearAll()
        setPubkey(null)
        setLoginType(null)
        return
      }
      localKeyRef.current = hexToBytes(hex)
    } catch (e) {
      console.warn('Failed to hydrate local key', e)
    }
  }, [loginType])

  // For encrypted and Google accounts, try to restore the decrypted key
  // from sessionStorage so a refresh stays signed in. sessionStorage is
  // per-tab and cleared on tab close — unlocked for this browser
  // session, locked again on restart. The on-disk material (encrypted
  // key for local-encrypted; nothing for google — the Drive is the
  // source) is unchanged.
  //
  // Sets `restoring` to false at every exit so the unlock-modal
  // auto-open gate in Home.jsx unblocks once we've made a decision.
  useEffect(() => {
    const isSessionCached =
      loginType === 'local-encrypted' || loginType === 'google'
    if (!isSessionCached || !locked) {
      if (restoring) setRestoring(false)
      return
    }
    if (!pubkey) {
      setRestoring(false)
      return
    }
    const cached = loadSessionDecryptedKey()
    if (!cached) {
      setRestoring(false)
      return
    }
    try {
      const cachedPk = getPublicKey(cached)
      if (cachedPk !== pubkey) {
        // Pubkey mismatch — stale session cache, clear it.
        clearSessionDecryptedKey()
        setRestoring(false)
        return
      }
      localKeyRef.current = cached
      setLocked(false)
    } catch (e) {
      console.warn('Failed to restore session-cached key', e)
      clearSessionDecryptedKey()
    } finally {
      setRestoring(false)
    }
  }, [loginType, pubkey, locked, restoring])

  // UX29: Google OAuth callback is handled here on mount. The hash
  // fragment #google_auth=<base64url(JSON)> arrives on return from the
  // serverless OAuth callback (api/google-oauth-callback.js). parseOAuth
  // CallbackHash() reads it and immediately clears the URL via
  // history.replaceState, so this effect sees it exactly once per page
  // load even under React StrictMode double-invoke.
  //
  // Two branches:
  //   1. Linking flow — sessionStorage carries a fresh verdant_google
  //      _linking payload (Phase 2c). We encrypt the caller's existing
  //      nsec with the server-derived key and write it to Drive,
  //      replacing any prior ciphertext only after user confirms.
  //   2. Sign-in flow (default) — fetch / create the Drive-stored
  //      identity file, decrypt the nsec, sign in as loginType='google'.
  //
  // Pubkey + loginType persist to localStorage so the account chip
  // survives refresh; the decrypted key goes to sessionStorage (same
  // pattern as local-encrypted — tab close re-triggers OAuth). The
  // nsec never touches localStorage; Drive is the only durable store.
  useEffect(() => {
    const result = parseOAuthCallbackHash()
    if (!result) return
    if (!result.ok) {
      setError(googleErrorMessage(result.reason))
      try { sessionStorage.removeItem(SS_LINK_FLAG) } catch { /* noop */ }
      return
    }

    // Linking-flag check — always clear the flag on read so a stale
    // (expired or malformed) one can't hijack a later sign-in.
    let linkingPayload = null
    try {
      const raw = sessionStorage.getItem(SS_LINK_FLAG)
      if (raw) {
        sessionStorage.removeItem(SS_LINK_FLAG)
        const parsed = JSON.parse(raw)
        const fresh =
          typeof parsed?.initiatedAt === 'number' &&
          Date.now() - parsed.initiatedAt < LINK_FLAG_TTL_MS
        if (
          fresh &&
          typeof parsed.nsecHex === 'string' &&
          typeof parsed.pubkey === 'string'
        ) {
          linkingPayload = parsed
        }
      }
    } catch { /* noop */ }

    let cancelled = false
    ;(async () => {
      try {
        if (linkingPayload) {
          await finishLinking(linkingPayload, result, cancelled)
        } else {
          await finishSignIn(result, cancelled)
        }
      } catch (e) {
        if (cancelled) return
        const op = linkingPayload ? 'google_link' : 'google_signin'
        setError(
          linkingPayload
            ? 'Could not finish Google linking. Try again.'
            : 'Could not finish Google sign-in. Try again.'
        )
        Sentry.withScope((scope) => {
          scope.setTag('op', op)
          Sentry.captureException(e)
        })
      }
    })()
    return () => { cancelled = true }

    async function finishSignIn(result, cancelled) {
      const nsecStr = await resolveNostrIdentity(result, () => {
        const sk = generateSecretKey()
        return nip19.nsecEncode(sk)
      })
      if (cancelled) return
      const decoded = nip19.decode(nsecStr)
      if (decoded.type !== 'nsec') throw new Error('Invalid key format')
      const secretKey = decoded.data
      const pk = getPublicKey(secretKey)
      clearLocalSecrets()
      persistPubkey(pk, 'google')
      localKeyRef.current = secretKey
      persistSessionDecryptedKey(secretKey)
      // UX44: cache the Drive token so subsequent prefs writes don't
      // need to re-OAuth. Also sync any saved prefs down to this
      // device.
      persistDriveToken(result.drv, result.exp)
      try {
        const prefs = await readPrefsFile({ drv: result.drv })
        if (prefs?.jurisdiction) {
          saveJurisdiction(prefs.jurisdiction)
        }
      } catch { /* non-fatal */ }
      setPubkey(pk)
      setLoginType('google')
      setLocked(false)
      Sentry.setUser({ id: pk })
      track('signin_completed', { method: 'google' })
    }

    async function finishLinking(payload, result, cancelled) {
      const { nsecHex, pubkey: linkedPubkey } = payload
      const secretKey = hexToBytes(nsecHex)
      const linkedNsec = nip19.nsecEncode(secretKey)

      // Check whether another identity is already parked on this
      // Google account; policy is (b) warn-and-replace per UX29 2c.
      const { fileId, existingNsec } = await readLinkedDriveFile({
        drv: result.drv,
        enc: result.enc,
      })
      if (cancelled) return

      if (existingNsec && existingNsec !== linkedNsec) {
        const existingDecoded = nip19.decode(existingNsec)
        const existingPubkey =
          existingDecoded?.type === 'nsec'
            ? getPublicKey(existingDecoded.data)
            : null
        if (existingPubkey && existingPubkey !== linkedPubkey) {
          const ok = window.confirm(
            "Another Nostr identity is already stored in your Google Drive. " +
            "Replacing it means that if you sign in with Google later, you'll " +
            "see this account, not the previous one. Replace it?"
          )
          if (!ok) {
            setError('Linking canceled.')
            return
          }
        }
      }

      await writeLinkedDriveFile({
        drv: result.drv,
        enc: result.enc,
        nsec: linkedNsec,
        existingFileId: fileId,
      })
      if (cancelled) return

      // Local state swap: same pubkey, same in-memory key; just flip
      // loginType and purge local secrets since Drive is now the
      // durable store.
      clearLocalSecrets()
      persistPubkey(linkedPubkey, 'google')
      setLoginType('google')
      // UX44: cache token + sync current jurisdiction up to Drive so
      // the user who just saved sees their location follow them.
      persistDriveToken(result.drv, result.exp)
      track('signin_completed', { method: 'google_link' })
    }
  }, [])

  const loginWithGoogle = useCallback(() => {
    setError(null)
    try {
      startGoogleOAuth()
      // Browser is now redirecting to Google; code after this is
      // unreachable in the current tab.
    } catch (e) {
      setError(e?.message || 'Could not start Google sign-in.')
    }
  }, [])

  // UX29 Phase 2c: link the currently-signed-in local account to
  // Google. Only usable when we have an in-memory nsec (loginType
  // 'local' or 'local-encrypted' and not locked). We stash the nsec
  // in sessionStorage under SS_LINK_FLAG so the OAuth-callback
  // useEffect can finish the write after Google redirects back.
  const linkWithGoogle = useCallback(() => {
    setError(null)
    if (!localKeyRef.current || !pubkey) {
      setError('Sign in with your own key first before linking Google.')
      return
    }
    try {
      sessionStorage.setItem(SS_LINK_FLAG, JSON.stringify({
        nsecHex: bytesToHex(localKeyRef.current),
        pubkey,
        initiatedAt: Date.now(),
      }))
      startGoogleOAuth()
    } catch (e) {
      try { sessionStorage.removeItem(SS_LINK_FLAG) } catch { /* noop */ }
      setError(e?.message || 'Could not start Google linking.')
    }
  }, [pubkey])

  const loginNip07 = useCallback(async () => {
    setError(null)
    if (!window?.nostr) {
      setError('No NIP-07 extension found. Install Alby or Nos2x to continue.')
      return null
    }
    try {
      const pk = await window.nostr.getPublicKey()
      clearLocalSecrets()
      persistPubkey(pk, 'nip07')
      localKeyRef.current = null
      setPubkey(pk)
      setLoginType('nip07')
      setLocked(false)
      Sentry.setUser({ id: pk })
      track('signin_completed', { method: 'nip07' })
      return pk
    } catch (e) {
      setError(e?.message || 'Login was rejected.')
      return null
    }
  }, [])

  const importKey = useCallback(async (input, options = {}) => {
    setError(null)
    let secretKey
    try {
      const trimmed = (input || '').trim()
      if (trimmed.startsWith('nsec1')) {
        const decoded = nip19.decode(trimmed)
        if (decoded.type !== 'nsec') throw new Error('That is not an nsec.')
        secretKey = decoded.data
      } else if (/^[0-9a-f]{64}$/i.test(trimmed)) {
        secretKey = hexToBytes(trimmed)
      } else if (trimmed.startsWith('ncryptsec1')) {
        if (!options.passphrase) {
          throw new Error('Enter the passphrase for this encrypted key.')
        }
        secretKey = nip49.decrypt(trimmed, options.passphrase)
      } else {
        throw new Error('Paste an nsec, ncryptsec, or 64-char hex private key.')
      }
    } catch (e) {
      setError(e?.message || 'Invalid key format.')
      return null
    }

    try {
      const pk = getPublicKey(secretKey)
      persistLocalKey(secretKey, pk, options)
      localKeyRef.current = secretKey
      // For encrypted accounts, cache the decrypted key in sessionStorage
      // so refreshes stay unlocked. Tab close clears it.
      if (options.passphrase) persistSessionDecryptedKey(secretKey)
      setPubkey(pk)
      setLoginType(options.passphrase ? 'local-encrypted' : 'local')
      setLocked(false)
      Sentry.setUser({ id: pk })
      track('signin_completed', { method: 'import' })
      return pk
    } catch (e) {
      setError(e?.message || 'Failed to import key.')
      return null
    }
  }, [])

  // UX41: silently generate an ephemeral key on first vote so the user
  // can participate without seeing the auth modal. Returns the pubkey
  // on success so the caller (handleVote) can use it in the vote event
  // without waiting for React state to flush. loginType is 'ephemeral',
  // which is treated like 'local' everywhere (plaintext hex nsec in
  // localStorage, hydrated on mount, exportable from AccountModal) but
  // surfaces different UI framing (Anonymous chip, Save-your-account
  // prompt). The user can later upgrade via linkWithGoogle to bind
  // this same key to a Google account.
  const beginEphemeral = useCallback(() => {
    if (localKeyRef.current && pubkey) {
      // Already have an in-memory key (e.g. user signed in during a
      // race). Caller can proceed with the current pubkey.
      return pubkey
    }
    try {
      const secretKey = generateSecretKey()
      const pk = getPublicKey(secretKey)
      // Persist like a plaintext 'local' key, but tag loginType as
      // ephemeral so the UI can distinguish.
      try {
        localStorage.setItem(LS_PUBKEY, pk)
        localStorage.setItem(LS_NSEC, bytesToHex(secretKey))
        localStorage.setItem(LS_LOGIN_TYPE, 'ephemeral')
        localStorage.removeItem(LS_NCRYPTSEC)
      } catch { /* quota — vote still works for this tab */ }
      localKeyRef.current = secretKey
      setPubkey(pk)
      setLoginType('ephemeral')
      setLocked(false)
      Sentry.setUser({ id: pk })
      track('signin_completed', { method: 'ephemeral' })
      return pk
    } catch (e) {
      setError(e?.message || 'Could not start anonymous session.')
      return null
    }
  }, [pubkey])

  const createNew = useCallback((options = {}) => {
    setError(null)
    try {
      const secretKey = generateSecretKey()
      const pk = getPublicKey(secretKey)
      persistLocalKey(secretKey, pk, options)
      localKeyRef.current = secretKey
      if (options.passphrase) persistSessionDecryptedKey(secretKey)
      setPubkey(pk)
      setLoginType(options.passphrase ? 'local-encrypted' : 'local')
      setLocked(false)
      Sentry.setUser({ id: pk })
      track('signin_completed', { method: 'create' })
      return {
        pubkey: pk,
        nsec: nip19.nsecEncode(secretKey),
      }
    } catch (e) {
      setError(e?.message || 'Failed to create account.')
      return null
    }
  }, [])

  const unlock = useCallback((passphrase) => {
    setError(null)
    try {
      const ncryptsec = localStorage.getItem(LS_NCRYPTSEC)
      if (!ncryptsec) {
        setError('No encrypted key on file.')
        return false
      }
      const secretKey = nip49.decrypt(ncryptsec, passphrase)
      const pk = getPublicKey(secretKey)
      const stored = localStorage.getItem(LS_PUBKEY)
      if (stored && stored !== pk) {
        setError('Decrypted key does not match the stored public key.')
        return false
      }
      localKeyRef.current = secretKey
      persistSessionDecryptedKey(secretKey)
      setPubkey(pk)
      setLoginType('local-encrypted')
      setLocked(false)
      Sentry.setUser({ id: pk })
      track('signin_completed', { method: 'unlock' })
      return true
    } catch (e) {
      setError('Wrong passphrase.')
      return false
    }
  }, [])

  const logout = useCallback(() => {
    clearAll()
    clearDriveToken()
    localKeyRef.current = null
    setPubkey(null)
    setLoginType(null)
    setLocked(false)
    Sentry.setUser(null)
  }, [])

  // Lock the session without logging out — keeps the encrypted key on disk
  // but drops the in-memory secret. Useful for "lock screen" UX. Also
  // clears the sessionStorage cache so a refresh re-prompts as expected.
  const lock = useCallback(() => {
    if (loginType !== 'local-encrypted') return
    localKeyRef.current = null
    clearSessionDecryptedKey()
    setLocked(true)
  }, [loginType])

  // Reveal the user's nsec for backup. Only available for local accounts.
  const exportNsec = useCallback(() => {
    if (!localKeyRef.current) return null
    try {
      return nip19.nsecEncode(localKeyRef.current)
    } catch {
      return null
    }
  }, [])

  const sign = useCallback(async (template) => {
    if (loginType === 'nip07') {
      if (!window?.nostr) throw new Error('NIP-07 extension is not available.')
      return window.nostr.signEvent(template)
    }
    if (locked) throw new Error('Account is locked. Enter your passphrase.')
    const sk = localKeyRef.current
    if (!sk) throw new Error('Not signed in.')
    return finalizeEvent(template, sk)
  }, [loginType, locked])

  const npub = pubkey ? safeEncodeNpub(pubkey) : null

  return {
    // identity
    pubkey,
    npub,
    loginType,
    isLoggedIn: !!pubkey && !locked,
    locked,
    restoring,

    // capability flags
    nostrAvailable,
    canExportNsec: loginType === 'local'
      || loginType === 'ephemeral'
      || loginType === 'google'
      || (loginType === 'local-encrypted' && !locked),
    canLinkGoogle: !!localKeyRef.current
      && (loginType === 'local'
        || loginType === 'local-encrypted'
        || loginType === 'ephemeral')
      && !locked,
    isEphemeral: loginType === 'ephemeral',

    // actions
    loginNip07,
    loginWithGoogle,
    linkWithGoogle,
    beginEphemeral,
    importKey,
    createNew,
    unlock,
    lock,
    logout,
    exportNsec,
    sign,

    // diagnostic
    error,
    clearError: () => setError(null),
  }
}

// Human-readable messages for the known Google OAuth error reason codes
// surfaced by the serverless callback. Lives outside the hook body so
// the strings aren't recreated on every render.
function googleErrorMessage(reason) {
  switch (reason) {
    case 'google_denied': return 'Google sign-in was canceled.'
    case 'state_mismatch': return 'Sign-in state did not verify. Try again.'
    case 'config_missing': return 'Google sign-in is not configured on this deployment.'
    case 'exchange_failed': return 'Google could not confirm your sign-in. Try again.'
    case 'malformed_response': return 'Google returned an unexpected response. Try again.'
    case 'kdf_failed': return 'Could not derive your account key. Try again.'
    case 'missing_params': return 'Google sign-in was interrupted. Try again.'
    default: return 'Google sign-in did not complete. Try again.'
  }
}

// ---------- module-private helpers ----------

function loadInitial() {
  try {
    return {
      pubkey: localStorage.getItem(LS_PUBKEY),
      type: localStorage.getItem(LS_LOGIN_TYPE),
    }
  } catch {
    return { pubkey: null, type: null }
  }
}

function persistPubkey(pubkey, type) {
  try {
    localStorage.setItem(LS_PUBKEY, pubkey)
    localStorage.setItem(LS_LOGIN_TYPE, type)
  } catch { /* noop */ }
}

function persistLocalKey(secretKey, pubkey, options) {
  try {
    localStorage.setItem(LS_PUBKEY, pubkey)
    if (options.passphrase) {
      // NIP-49 ncryptsec — scrypt-based, intentionally slow (~1–2s).
      const ncryptsec = nip49.encrypt(secretKey, options.passphrase)
      localStorage.setItem(LS_NCRYPTSEC, ncryptsec)
      localStorage.setItem(LS_LOGIN_TYPE, 'local-encrypted')
      localStorage.removeItem(LS_NSEC)
    } else {
      localStorage.setItem(LS_NSEC, bytesToHex(secretKey))
      localStorage.setItem(LS_LOGIN_TYPE, 'local')
      localStorage.removeItem(LS_NCRYPTSEC)
    }
  } catch (e) {
    console.warn('Failed to persist local key', e)
  }
}

function clearLocalSecrets() {
  try {
    localStorage.removeItem(LS_NSEC)
    localStorage.removeItem(LS_NCRYPTSEC)
  } catch { /* noop */ }
}

function clearAll() {
  try {
    localStorage.removeItem(LS_PUBKEY)
    localStorage.removeItem(LS_LOGIN_TYPE)
    localStorage.removeItem(LS_NSEC)
    localStorage.removeItem(LS_NCRYPTSEC)
  } catch { /* noop */ }
  clearSessionDecryptedKey()
}

function persistSessionDecryptedKey(secretKey) {
  if (typeof sessionStorage === 'undefined') return
  try { sessionStorage.setItem(SS_DECRYPTED_KEY, bytesToHex(secretKey)) }
  catch { /* noop */ }
}

function loadSessionDecryptedKey() {
  if (typeof sessionStorage === 'undefined') return null
  try {
    const hex = sessionStorage.getItem(SS_DECRYPTED_KEY)
    if (!hex) return null
    return hexToBytes(hex)
  } catch { return null }
}

function clearSessionDecryptedKey() {
  if (typeof sessionStorage === 'undefined') return
  try { sessionStorage.removeItem(SS_DECRYPTED_KEY) } catch { /* noop */ }
}

function safeEncodeNpub(hex) {
  try { return nip19.npubEncode(hex) } catch { return null }
}

function hexToBytes(hex) {
  const clean = hex.startsWith('0x') ? hex.slice(2) : hex
  if (clean.length % 2) throw new Error('Bad hex length')
  const out = new Uint8Array(clean.length / 2)
  for (let i = 0; i < out.length; i++) {
    out[i] = parseInt(clean.substr(i * 2, 2), 16)
  }
  return out
}

function bytesToHex(bytes) {
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')
}
